import * as React from "react";

import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ButtonStack } from "@oscar/common-ui";
import { clsx } from "clsx";
import { noop } from "lodash";

import { POPUP_CLASSES } from "~/components/pop-up/constants";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { Close24 } from "~/theme/icons/components/close";
import { getFormatMedia } from "~/theme/utils";

import { PopUpContainer } from "../../styled";

import type { ConfirmationPopUpProps } from "./types";

export const ConfirmationPopUp = (
  props: ConfirmationPopUpProps,
): React.ReactElement => {
  const {
    open,
    handleClose,
    handleConfirm,
    handleOtherAction,
    title,
    description,
    deleteButtonText,
    cancelButtonText,
    cancelButtonName,
    otherActionText,
    disableCloseButton = false,
  } = props;

  const theme = useTheme();
  const { BREAKPOINT_TABLET } = getFormatMedia(theme);
  const isMobileView = !useMediaQuery(BREAKPOINT_TABLET);

  return (
    <PopUpContainer
      open={open}
      slotProps={{
        paper: {
          classes: { root: POPUP_CLASSES.root },
        },
      }}
      onClose={!disableCloseButton ? handleClose : noop}
      fullWidth
      {...getTestAutomationProps("confirmation-pop-up")}
    >
      <DialogTitle
        data-cs-override-id=""
        className={clsx(
          POPUP_CLASSES.title,
          disableCloseButton && POPUP_CLASSES.titleWithDisabledCloseButton,
        )}
      >
        <Box>
          <Typography variant="h4">{title}</Typography>
          {!disableCloseButton && (
            <IconButton
              onClick={handleClose}
              className={POPUP_CLASSES.closeButton}
              {...getTestAutomationProps("close-button")}
              size="large"
              aria-label={`close-button ${title}`}
            >
              <Close24 />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent className={POPUP_CLASSES.content}>
        <DialogContentText className={POPUP_CLASSES.description}>
          {description}
        </DialogContentText>
      </DialogContent>
      <DialogActions disableSpacing className={POPUP_CLASSES.actions}>
        <ButtonStack
          variant={isMobileView ? "vertical" : "horizontal"}
          size="medium"
          buttons={
            handleOtherAction
              ? [
                  {
                    type: "secondary",
                    display: true,
                    buttonText: otherActionText ?? "",
                    onClickFn: handleOtherAction,
                  },
                  {
                    type: "primary",
                    display: true,
                    buttonText: deleteButtonText ?? "",
                    onClickFn: handleConfirm,
                    ...getTestAutomationProps("confirm-button"),
                  },
                ]
              : [
                  {
                    type: "secondary",
                    display: true,
                    buttonText: cancelButtonText ?? cancelButtonName ?? "",
                    onClickFn: handleClose,
                  },
                  {
                    type: "primary",
                    display: true,
                    buttonText: deleteButtonText ?? "",
                    onClickFn: handleConfirm,
                    ...getTestAutomationProps("confirm-button"),
                  },
                ]
          }
        />
      </DialogActions>
    </PopUpContainer>
  );
};
